import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, map, tap, filter, combineLatest } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private auth0: Auth0Service,
    private router: Router,
    private logger: NGXLogger
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    // Aspetta che Auth0 abbia finito di caricare lo stato prima di decidere
    return combineLatest([
      this.auth0.isLoading$,
      this.authService.isAuthenticated$
    ]).pipe(
      // Aspetta che Auth0 abbia finito di caricare (isLoading === false)
      filter(([isLoading]) => !isLoading),
      // Prendi solo il primo valore dopo che ha finito di caricare
      map(([isLoading, isAuthenticated]) => isAuthenticated),
      tap(isAuthenticated => {
        if (!isAuthenticated) {
          this.logger.debug('Accesso negato - utente non autenticato, reindirizzamento alla landing');
          // Usa window.location per ottenere l'URL completo incluso i query params
          // Questo è più affidabile quando si naviga direttamente da URL
          const currentPath = window.location.pathname;
          const currentSearch = window.location.search;
          const fullUrl = currentPath + (currentSearch ? currentSearch : '');

          // Preserva i query params e la rotta originale per poterci tornare dopo il login
          this.router.navigate(['/'], {
            queryParams: { returnUrl: fullUrl }
          });
        }
      })
    );
  }
}

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private logger: NGXLogger
  ) {}

  canActivate(route: any): Observable<boolean> {
    const requiredRoles = route.data?.['roles'] as string[];

    if (!requiredRoles || requiredRoles.length === 0) {
      return this.authService.isAuthenticated$;
    }

    // Controlla prima i ruoli da Auth0
    return this.authService.hasAnyRole(requiredRoles).pipe(
      map(hasRoleFromAuth0 => {
        // Se ha il ruolo da Auth0, permette l'accesso
        if (hasRoleFromAuth0) {
          return true;
        }
        
        // Fallback: controlla anche il ruolo in localStorage
        // Questo è necessario perché il sistema usa localStorage per i ruoli
        const ruoloInStorage = localStorage.getItem('ruolo');
        if (ruoloInStorage && requiredRoles.includes(ruoloInStorage)) {
          this.logger.debug('Accesso consentito tramite ruolo in localStorage:', ruoloInStorage);
          return true;
        }
        
        return false;
      }),
      tap(hasRole => {
        if (!hasRole) {
          this.logger.debug('Accesso negato - ruolo insufficiente. Ruolo richiesto:', requiredRoles, 'Ruolo in storage:', localStorage.getItem('ruolo'));
          this.router.navigate(['/unauthorized']);
        }
      })
    );
  }
}
